<template>
  <div>
    <FilterOverview title="Reiseterminplanung" :showFilterInitially="false" @reload="onReload">
      <template #toolbar-left>
        <div
          class="d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap"
          style="gap: 12px"
        >
          <RegionFilterButtonGroup
            :regions="['EU', 'FERN', 'DACH']"
            :disabled="regionFilterDisabled"
            v-model="selectedRegions"
            width="150px"
          ></RegionFilterButtonGroup>

          <b-form-checkbox v-model="vergangeneTermineAusblenden" @change="gridApi.onFilterChanged()" switch>
            Vergangene Termine ausblenden
          </b-form-checkbox>

          <!-- Filter Reset Button -->
          <div
            v-if="isFilterSupplied"
            class="icon-button d-flex justify-content-center text-danger mr-4 ml-2"
            @click="resetAllFilters"
            v-b-tooltip.hover.bottom
            title="Filter zurücksetzen"
          >
            <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
          </div>
        </div>
      </template>
      <template #toolbar-right>
        <b-button
          size="sm"
          class="icon-button d-inline-flex align-items-center justify-content-center p-0 mr-2"
          v-b-tooltip.hover.bottom="{ customClass: 'tooltip-opacity' }"
          title="Termin anlegen"
          v-b-modal.modalReiseterminAnlegen
        >
          <CalendarPlus :size="20" />
        </b-button>

        <b-dropdown
          id="terminplanung-aktionen"
          right
          no-caret
          size="sm"
          toggle-class="icon-button d-inline-flex align-items-center justify-content-center p-0"
          menu-class="aktionen-dropdown"
          :toggle-attrs="{ 'aria-label': 'Weitere Aktionen' }"
        >
          <template #button-content>
            <Ellipsis :size="20" />
          </template>
          <b-dropdown-item
            v-for="option in statuswechselSelektierte.statusOptionen"
            :key="option.value"
            href="#"
            @click="onStatusWechseln({ reiseterminIds: idsSelectedReisetermine, option })"
          >
            {{ option.label }}
          </b-dropdown-item>
          <b-dropdown-divider v-if="statuswechselSelektierte.statusOptionen.length" />
          <b-dropdown-item
            v-if="statuswechselSelektierte.verwerfenErlaubt"
            href="#"
            @click="onTerminVerwerfen({ reiseterminIds: idsSelectedReisetermine })"
          >
            Termin verwerfen
          </b-dropdown-item>
          <b-dropdown-item v-if="onlyOneReiseSelected" href="#" v-b-modal.modalMultipleReiseterminBearbeiten>
            Daten bearbeiten
          </b-dropdown-item>
          <b-dropdown-divider v-if="statuswechselSelektierte.verwerfenErlaubt || onlyOneReiseSelected" />
          <b-dropdown-item
            size="sm"
            @click="onClickDeleteReisetermine"
            variant="danger"
            v-if="
              allSelectedRows.length > 0 &&
              allSelectedRows.every(item => item.data.reiseterminSyncState == 'NotAvailableInBlank')
            "
          >
            Reisetermine löschen
          </b-dropdown-item>
          <b-dropdown-item>
            <div class="d-flex align-items-center justify-content-between w-100">
              <span>Terminexport</span>
              <div class="d-flex" style="gap: 8px">
                <button
                  type="button"
                  class="pill-badge pill-badge--neutral pill-badge--button"
                  @click="onButtonExportExtern('de')"
                >
                  DE
                </button>
                <button
                  type="button"
                  class="pill-badge pill-badge--neutral pill-badge--button"
                  @click="onButtonExportExtern('en')"
                >
                  EN
                </button>
              </div>
            </div>
          </b-dropdown-item>
        </b-dropdown>
        <b-tooltip target="terminplanung-aktionen__BV_toggle_" triggers="hover" placement="bottom">
          Weitere Aktionen
        </b-tooltip>
        <div
          class="icon-button d-inline-flex justify-content-center align-middle ml-2"
          @click="openAufgabenModalWithData"
          v-b-tooltip.hover.bottom
          title="Aufgabe erstellen"
        >
          <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
        </div>
      </template>
      <template #table="{ tableHeight }">
        <AgGridVue
          :style="{ height: tableHeight + 87 + 'px' }"
          rowHeight="50px"
          class="ag-theme-alpine m-0 p-0"
          @grid-ready="onGridReady"
          :rowModelType="'serverSide'"
          :getRowId="getRowId"
          :statusBar="statusBar"
          :columnDefs="columnDefs"
          :tooltipShowDelay="0"
          :defaultColDef="defaultColDef"
          suppressCellFocus
          :enableCellTextSelection="true"
          suppressContextMenu
          :rowSelection="'multiple'"
          :rowMultiSelectWithClick="'true'"
          @rowSelected="onRowSelected"
          @rowDoubleClicked="onRowDoubleClicked"
        ></AgGridVue>
        <ReiseterminAnlegenModal @reiseterminAnlegen="gridApi.onFilterChanged()"></ReiseterminAnlegenModal>
        <MultipleReiseterminBearbeitenModal
          :title="'Daten der Reise ' + reiseOfSelectedRows + ' bearbeiten'"
          :reiseterminEditData="multipleReiseterminEditData"
          @reiseterminValuesBearbeitet="onReiseterminValuesBearbeitetMultiple"
          @reiseterminMetadataBearbeitet="onReiseterminMetadataBearbeitetMultiple"
        ></MultipleReiseterminBearbeitenModal>
        <TerminplanungSidebar
          ref="terminplanungSidebar"
          :reiseterminId="activeReisetermin?.id"
          @sidebarClosed="onSidebarClosed"
          @reload="onReload"
          @rowSelectionChanged="changeRowSelection"
          @reiseterminMetadataBearbeitet="onReiseterminMetadataBearbeitet"
          @reiseterminBearbeitet="onReiseterminBearbeitet"
          @inlandsflugBearbeitet="onInlandsflugBearbeitet"
          @showModalAufgabeEdit="onShowModalAufgabeEdit"
          @deletedInlandsflug="onDeletedInlandsflug"
          @createdInlandsflug="onCreatedInlandsflug"
          @reiseterminLoeschen="onDeleteReisetermin"
          @reiseterminAngelegt="gridApi.onFilterChanged()"
          @statusWechseln="onStatusWechseln"
          @terminVerwerfen="onTerminVerwerfen"
        ></TerminplanungSidebar>
        <ModalStatusBestaetigen
          id="modalStatusBestaetigen"
          :statusText="statuswechselPending.option?.text"
          @confirm="confirmStatusWechsel"
        />
        <ModalTerminVerwerfen id="modalTerminVerwerfen" @verwerfen="confirmTerminVerwerfen" />
        <AufgabeErstellenModal
          :prefixedVerlinkungen="prefixedVerlinkungen"
          :disableAttachments="false"
          :fromFVC="false"
          :enableManualTask="true"
        />
        <DeleteConfirmationModal
          id="modalReisetermineLoeschen"
          title="Reisetermine löschen?"
          entityBezeichnung="die ausgewählten Reisetermine"
          @confirm="confirmDeleteReisetermine"
        >
          Möchtest du die Reisetermine
          <template v-for="(row, index) in allSelectedRows">
            <br :key="'br-' + index" />
            <strong :key="index">{{ row.data.reiseterminkuerzel }}</strong>
          </template>
          wirklich löschen?
        </DeleteConfirmationModal>
      </template>
    </FilterOverview>
  </div>
</template>

<script>
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import FilterOverview from '@/components/common/filter-overview.vue';
import apiService from '@/core/common/services/api.service';
import buildQuery from 'odata-query';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';
import AgDateRangePicker from '@/components/flugverfuegbarkeit/ag-date-range-picker.vue';
import CustomHeader from '@/components/flugverfuegbarkeit/custom-header.vue';
import {
  fernRegions,
  euRegions,
  formatDateWithoutHours,
  getBlankSyncState,
  reiseterminStateFormatter,
  reiseterminStatePillHtml,
  reiseterminStateFormatterSmallHtml,
  zusatzterminBadgeHtml,
} from '@/core/common/helpers/utils.js';
import {
  terminplanungHeaderRow,
  terminplanungSelectedReiseterminVariables,
  terminplanungExpandConfig,
  terminplanungCmRueckmeldungSpalten,
  createExcelExport,
} from '@/core/common/helpers/exportHelper.js';
import AktionenRenderer from '@/components/produkte/reisetermine/aktionen-renderer.vue';
import ReiseterminAnlegenModal from '@/components/produkte/reisetermine/reisetermin-anlegen-modal.vue';
import MultipleReiseterminBearbeitenModal from '@/components/produkte/reisetermine/multiple-reisetermin-bearbeiten-modal.vue';
import StatusBarComponent from '@/components/flugverfuegbarkeit/status-bar-ag-grid.vue';
import { add, parseISO } from 'date-fns';
import { mapGetters } from 'vuex';
import TerminplanungSidebar from '@/components/produkte/reisetermine/sidebar-terminplanung/sidebar-terminplanung.vue';
import ModalStatusBestaetigen from '@/components/produkte/reisetermine/modal-status-bestaetigen.vue';
import ModalTerminVerwerfen from '@/components/produkte/reisetermine/modal-termin-verwerfen.vue';
import { getStatuswechselOptionenMehrfach } from '@/core/produkte/reisetermine/reisetermin-statuswechsel';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import * as odataService from '@/core/common/services/odata.service';
import RegionFilterButtonGroup from '@/components/common/region-filter-button-group.vue';
import CustomInfoIconHeader from '@/components/common/custom-info-icon-header.vue';
import CellRendererPax from '@/components/produkte/reisetermine/cell-renderer-pax.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import { CalendarPlus, CircleCheck, Ellipsis, FunnelX, ListChecks, TriangleAlert, CircleX } from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';

export default {
  name: 'ReisenPlanningComponent',
  components: {
    CalendarPlus,
    ListChecks,
    Ellipsis,
    AgGridVue,
    FilterOverview,
    CustomTooltip,
    agColumnHeader: CustomHeader,
    AgDateRangePicker,
    StatusBarComponent,
    AktionenRenderer,
    ReiseterminAnlegenModal,
    TerminplanungSidebar,
    MultipleReiseterminBearbeitenModal,
    ModalStatusBestaetigen,
    ModalTerminVerwerfen,
    AufgabeErstellenModal,
    RegionFilterButtonGroup,
    CustomInfoIconHeader: CustomInfoIconHeader,
    CellRendererPax,
    DeleteConfirmationModal,
    FunnelX,
  },
  props: {
    tableHeight: {
      type: Number,
      required: false,
    },
  },

  data() {
    return {
      lastOdataFilter: null,
      // Betroffene Reisetermine und der gewählte Statuswechsel, bis der Bestätigungsdialog ihn auslöst
      statuswechselPending: {},
      gridApi: null,
      gridColumnApi: null,
      prefixedVerlinkungen: [],
      isFilterSupplied: false,
      columnDefs: null,
      vergangeneTermineAusblenden: true,
      defaultColDef: {
        floatingFilter: true,
        filter: true,
        flex: 1,
        resizable: false,
        sortable: false,
        suppressMenu: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        suppressMovable: true,
        filterParams: {
          defaultToNothingSelected: true,
        },
        tooltipComponent: 'CustomTooltip',
      },
      selectedRegions: [],
      regionFilterDisabled: true,
      allSelectedRows: [],
      count: null,
      statusBar: null,
      reiseterminEditData: null,
      activeReisetermin: null,
      selectedRowID: null,
      selectionToRestore: null,
      odataFilterReiseterminkuerzel: null,
      defaultStatusFilter: [
        'Vorgeschlagen',
        'Aufgelegt',
        'Anlagebereit',
        'CmBestaetigt',
        'CmAngefragt',
        'CmAbgelehnt',
      ],
    };
  },
  watch: {
    // schaut auf das array "selectedRegions" und dessen veränderung
    selectedRegions(newSelectedRegions) {
      // und geben das newSelectedRegions aus dem emit in die methode
      this.changeRegionFilterInstance(newSelectedRegions);
    },
    vergangeneTermineAusblenden(newValue) {
      this.updateStatusFilter(newValue);
    },
  },
  created() {
    this.columnDefs = [
      {
        field: 'istZusatztermin',
        cellRenderer: params => zusatzterminBadgeHtml(params.value),
        cellStyle: { textAlign: 'center' },
        filterParams: {
          values: [true, false],
          valueFormatter: params =>
            params.value === true
              ? 'Zusatztermin'
              : params.value === false
              ? 'Kein Zusatztermin'
              : params.value,
        },
        headerName: '',
        pinned: 'left',
        width: 50,
        minWidth: 50,
      },
      {
        field: 'reiseterminkuerzel',
        headerName: 'Reise',
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          values: params => params.success(this.reisenOptions),
          debounceMs: 1000,
        },
        valueFormatter: params => params.value.substring(0, 6),
        checkboxSelection: true,
        headerCheckboxSelection: true,
        sortable: true,
        pinned: 'left',
        minWidth: 150,
        width: 150,
      },
      {
        field: 'state',
        headerName: 'Status',
        filter: 'agSetColumnFilter',
        sortable: true,
        filterParams: {
          valueFormatter: params => reiseterminStateFormatter[params.value],
          cellRenderer: params => reiseterminStateFormatterSmallHtml[params.value] ?? params.value,
          values: Object.keys(reiseterminStateFormatter),
          debounceMs: 1000,
        },
        valueFormatter: params => reiseterminStateFormatter[params.value],
        cellRenderer: params => (params.value ? reiseterminStatePillHtml(params.value) : '-'),
        minWidth: 150,
        width: 150,
      },
      {
        field: 'istInKlaerung',
        headerName: 'In Klärung',
        sortable: false,
        cellRenderer: params =>
          params.data.istInKlaerung ? lucideHtml(TriangleAlert, { size: 20, class: 'text-warning' }) : '-',
        minWidth: 85,
        width: 85,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          cellRenderer: params =>
            params.value === '(Select All)'
              ? params.value
              : params.value
              ? lucideHtml(TriangleAlert, { class: 'text-warning' })
              : '-',
        },
      },
      {
        field: 'reiseterminSyncState',
        headerName: 'In Blank angelegt',
        cellRenderer: params => {
          const { value } = params;
          const label = getBlankSyncState(value);
          if (label === 'Angelegt') {
            return lucideHtml(CircleCheck, { size: 20, class: 'text-success' });
          } else if (label === 'Nicht synchronisiert') {
            return '-';
          }
          return lucideHtml(CircleX, { size: 20, class: 'text-danger' });
        },
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: {
          values: [true, false, null],
          cellRenderer: params => {
            if (params.value === '(Select All)') {
              return params.value;
            } else if (params.value) {
              return `<span class='d-flex align-items-center'>${lucideHtml(CircleCheck, {
                class: 'text-success',
              })}</span>`;
            } else if (params.value === false) {
              return `<span class='d-flex align-items-center'>${lucideHtml(CircleX, {
                class: 'text-danger',
              })}</span>`;
            }
            return '-';
          },
          debounceMs: 1000,
        },
        width: 135,
        minWidth: 135,
      },
      {
        field: 'abreisedatum',
        headerName: 'Abreise',
        sortable: true,
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        pinned: 'left',
        minWidth: 190,
        width: 190,
      },
      {
        field: 'verkehrstagHin',
        headerName: 'VT Hin',
        pinned: 'left',
        filter: 'agSetColumnFilter',
        filterParams: {
          debounceMs: 1000,
          values: [1, 2, 3, 4, 5, 6, 7],
        },
        width: 80,
        minWidth: 80,
      },
      {
        field: 'startdatum',
        headerName: 'Startdatum',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        sortable: true,
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 190,
        width: 190,
      },
      {
        field: 'metadata.hotelnaechte',
        headerName: 'Hotelnächte',
        minWidth: 120,
        width: 120,
        filter: false,
      },
      {
        field: 'reisedauer',
        headerName: 'Reisedauer',
        floatingFilter: false,
        minWidth: 120,
        width: 120,
      },
      {
        field: 'thLangstrecke',
        headerName: 'TH Langstrecke',
        cellRenderer: params => (params.value === null ? '-' : params.value + '€'),
        floatingFilter: false,
        minWidth: 150,
        width: 150,
      },
      {
        field: 'thInlandsflug',
        headerName: 'TH Inlandsflug',
        cellRenderer: params => (params.value === null ? '-' : params.value + '€'),
        floatingFilter: false,
        minWidth: 150,
        width: 150,
      },
      {
        field: 'metadata.overnightOutbound',
        headerName: 'OB',
        headerTooltip: 'Overnight Outbound',
        floatingFilter: false,
        cellRenderer: params => (params.value === null ? '-' : params.value),
        minWidth: 100,
        width: 100,
      },
      {
        field: 'metadata.overnightInbound',
        headerName: 'IB',
        headerTooltip: 'Overnight Inbound',
        floatingFilter: false,
        cellRenderer: params => (params.value === null ? '-' : params.value),
        minWidth: 100,
        width: 100,
      },
      {
        field: 'flightArrival',
        headerName: 'Flight Arrival',
        valueGetter: params => ({
          abreisedatum: params.data.abreisedatum,
          outbound: params.data.metadata?.overnightOutbound || 0,
        }),
        valueFormatter: params => {
          const { abreisedatum, outbound } = params.value;
          if (abreisedatum) {
            const date = add(parseISO(abreisedatum), { days: outbound });

            return formatDateWithoutHours(date);
          }
          return '-';
        },
        filter: false,
        minWidth: 130,
        width: 130,
      },
      {
        field: 'ziellandFlughaefen',
        headerName: 'Strecke',
        headerTooltip: 'Zielland Zielflughäfen - Zielland Abflughäfen',
        cellRenderer: params => {
          const ziellandZielflughaefen = params.data.metadata.ziellandZielflughaefen
            .map(item => item.flughafenIataCode)
            .join(', ');
          const ziellandAbflughaefen = params.data.metadata.ziellandAbflughaefen
            .map(item => item.flughafenIataCode)
            .join(', ');
          return `${ziellandZielflughaefen} - ${ziellandAbflughaefen}`;
        },
        floatingFilter: false,
        minWidth: 120,
        width: 120,
      },
      {
        field: 'hotelendUndRueckflugdatum',
        headerName: 'Rückflugdatum',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 190,
        width: 190,
      },
      {
        field: 'verkehrstagRueck',
        headerName: 'VT Rück',
        filter: 'agSetColumnFilter',
        filterParams: {
          debounceMs: 1000,
          values: [1, 2, 3, 4, 5, 6, 7],
        },
        width: 80,
        minWidth: 80,
      },
      {
        field: 'enddatum',
        headerName: 'Ankunft DE',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 190,
        width: 190,
        sortable: true,
      },
      {
        field: 'pax.ist',
        headerName: 'PAX',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        cellRenderer: 'CellRendererPax',
        filter: 'agTextColumnFilter',
        filterParams: {
          buttons: ['apply'],
        },
        sortable: false,
        minWidth: 165,
        width: 165,
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'justify-items': 'center',
          overflow: 'visible',
          'align-items': 'center',
        },
        cellRendererParams: params => ({
          paxObject: params.data.pax,
          minPax: params.data.metadata.minPax,
          maxPax: params.data.metadata.maxPax,
          kalkPax: params.data.metadata.kalkPax,
          forecastPax: params.data.metadata.forecastPax,
          durchfuehrbareMinPax: params.data.metadata.durchfuehrbareMinPax,
          id: params.data.id,
        }),
      },
      {
        field: 'metadata.erwarteteTermindurchfuehrungsquote',
        headerName: 'Erw. Termindurchführungsquote',
        cellRenderer: params => (params.value ? params.value + '%' : '-'),
        filter: false,
        sortable: false,
        minWidth: 250,
        width: 250,
      },
      {
        field: 'reise.region',
        headerName: 'Region',
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['FERN', 'EU', 'DACH'],
        },
      },
    ];

    this.statusBar = {
      statusPanels: [{ statusPanel: 'StatusBarComponent', key: 'statusBarCompKey', align: 'left' }],
    };
  },
  computed: {
    ...mapGetters(['getReisekuerzelOptions']),
    onlyOneReiseSelected() {
      return (
        this.allSelectedRows.length > 1 &&
        new Set(this.allSelectedRows.map(row => row.data.reiseterminkuerzel.substring(0, 6))).size === 1
      );
    },
    multipleReiseterminEditData() {
      if (this.allSelectedRows.length > 1) {
        return this.allSelectedRows.map(row => row.data);
      } else return [];
    },
    reisenOptions() {
      return this.getReisekuerzelOptions.map(r => r.reisekuerzel);
    },
    reiseOfSelectedRows() {
      if (
        this.allSelectedRows.length > 1 &&
        new Set(this.allSelectedRows.map(row => row.data.reiseterminkuerzel.substring(0, 6))).size === 1
      ) {
        return this.allSelectedRows[0].data.reiseterminkuerzel.substring(0, 6);
      }
      return '';
    },
    idsSelectedReisetermine() {
      if (this.allSelectedRows.length > 0) {
        return this.allSelectedRows.map(item => item.data.id);
      } else return [];
    },
    isOnlyOneReiseterminStateSelected() {
      return new Set(this.allSelectedRows.map(row => row.data.state)).size === 1;
    },
    stateSelectedReisetermine() {
      if (this.isOnlyOneReiseterminStateSelected) {
        return this.allSelectedRows[0].data.state;
      } else return '';
    },
    // Statuswechsel der Mehrfachauswahl, nur bei einheitlichem Status und einer Reise
    statuswechselSelektierte() {
      if (
        !this.allSelectedRows.length ||
        !this.onlyOneReiseSelected ||
        !this.isOnlyOneReiseterminStateSelected
      ) {
        return { statusOptionen: [], verwerfenErlaubt: false };
      }
      return getStatuswechselOptionenMehrfach(
        this.stateSelectedReisetermine,
        this.allSelectedRows.map(row => row.data.reiseterminSyncState)
      );
    },
  },
  mounted() {},
  methods: {
    getBlankSyncState,
    onSidebarClosed() {
      this.activeReisetermin = null;
      this.gridApi.deselectAll();
    },
    onShowModalAufgabeEdit(prefixedVerlinkungen) {
      this.prefixedVerlinkungen = prefixedVerlinkungen;
      this.$bvModal.show('modalAufgabeEdit');
    },
    openAufgabenModalWithData() {
      this.prefixedVerlinkungen = this.allSelectedRows.map((item, i) => ({
        id: i,
        typ: 'Reisetermin',
        artifactId: item.data.id,
        artifactText: item.data.reiseterminkuerzel,
      }));

      this.$bvModal.show('modalAufgabeEdit');
    },
    getRowId(params) {
      return String(params.data.id);
    },
    onReload() {
      // Die Auswahl wird gemerkt
      this.selectionToRestore = this.gridApi.getSelectedNodes().map(node => node.id);
      this.gridApi.refreshServerSide({ purge: true });
    },
    changeRowSelection(direction) {
      const displayedRows = this.gridApi?.getDisplayedRowCount();
      if (direction === 'up') {
        if (this.selectedRowID !== 0) {
          const targetedRowID = this.selectedRowID - 1;
          this.gridApi.forEachNode(rowNode => {
            if (rowNode.rowIndex === targetedRowID) {
              rowNode.setSelected(true, true);
              this.gridApi.ensureIndexVisible(rowNode.rowIndex);
              this.activeReisetermin = rowNode.data;
              this.selectedRowID = targetedRowID;
            }
          });
        }
      } else if (direction === 'down') {
        if (this.selectedRowID + 1 < displayedRows) {
          const targetedRowID = this.selectedRowID + 1;
          this.gridApi.forEachNode(rowNode => {
            if (rowNode.rowIndex === targetedRowID) {
              rowNode.setSelected(true, true);
              this.gridApi.ensureIndexVisible(rowNode.rowIndex);
              this.activeReisetermin = rowNode.data;
              this.selectedRowID = targetedRowID;
            }
          });
        }
      }
    },
    collapseSidebar() {
      this.$root.$emit('bv::toggle::collapse', 'sidebar-terminplanung');
    },

    // language steuert die Ja/Nein-Ausgabe der vier CM-Rückmeldungsspalten
    createExternRowData(rt, language) {
      const { metadata } = rt;

      return [
        rt.reiseterminkuerzel.substring(0, 6),
        rt.reiseterminkuerzel,
        new Date(rt.abreisedatum),
        metadata.overnightOutbound,
        new Date(rt.startdatum),
        metadata.hotelnaechte,
        new Date(rt.hotelendUndRueckflugdatum),
        metadata.overnightInbound,
        new Date(rt.enddatum),
        rt.state,
        ...terminplanungCmRueckmeldungSpalten(rt, language),
      ];
    },

    async onButtonExportExtern(language) {
      await createExcelExport({
        headerRow: terminplanungHeaderRow(language),
        fetchData: async () =>
          await odataService.getReisetermin({
            filter: this.lastOdataFilter,
            select: terminplanungSelectedReiseterminVariables.extern,
            count: true,
            expand: terminplanungExpandConfig,
            orderBy: 'startdatum asc',
          }),
        createRowData: rt => [this.createExternRowData(rt, language)],
        sheetName: 'Reiseterminplanung_Extern',
        fileNamePrefix: 'Reiseterminplanung_Export_Extern',
      });
    },

    onRowSelected($event) {
      this.allSelectedRows = this.getAllSelectedRows();
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setMarkedCount(this.allSelectedRows.length);
    },
    getAllSelectedRows() {
      const rowsSelected = [];
      this.gridApi.forEachNode((node, index) => {
        if (node.selected) {
          rowsSelected.push(node);
        }
      });
      return rowsSelected;
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      const statusFilterComponent = this.gridApi.getFilterInstance('state');
      statusFilterComponent.setModel({
        values: ['Vorgeschlagen', 'Aufgelegt', 'Anlagebereit', 'CmBestaetigt', 'CmAngefragt', 'CmAbgelehnt'],
      });

      const defaultSortModel = [
        { colId: 'reise.reisekuerzel', sort: 'asc', sortIndex: 0 },
        { colId: 'startdatum', sort: 'asc', sortIndex: 1 },
      ];

      params.columnApi.applyColumnState({ state: defaultSortModel });

      if (this.$route.query.reisetermine) {
        this.odataFilterReiseterminkuerzel = `reiseterminkuerzel in (${this.$route.query.reisetermine
          .split(',')
          .map(rt => `'${rt}'`)
          .join(',')})`;
      }

      if (this.$route.query.reise) {
        this.odataFilterReiseterminkuerzel = `reise/reisekuerzel in (${this.$route.query.reise
          .split(',')
          .map(rk => `'${rk}'`)
          .join(',')})`;
      }

      if (this.$route.query.abreisedatumVon && this.$route.query.abreisedatumBis) {
        this.vergangeneTermineAusblenden = false;
        this.$nextTick(() => {
          const abreisedatumFilter = this.gridApi.getFilterInstance('abreisedatum');
          if (abreisedatumFilter) {
            const dateFrom = new Date(this.$route.query.abreisedatumVon);
            const dateTo = new Date(this.$route.query.abreisedatumBis);
            abreisedatumFilter.onDateRangeFilterChanged([dateFrom, dateTo]);
          }
        });
      }

      this.gridApi?.onFilterChanged();

      const updateData = () => {
        const server = this.server();
        const datasource = this.createServerSideDatasource(server);
        // register the datasource with the grid
        params.api.setServerSideDatasource(datasource);
      };
      updateData();
    },
    createServerSideDatasource(server) {
      return {
        getRows: async params => {
          console.log('[Datasource] - rows requested by grid: ', params.request);
          const response = await server.getData(params.request);
          if (response.success) {
            // supply rows for requested block to grid
            params.success({ rowData: response.rows });
            this.updateStatusBar(this.gridApi?.getDisplayedRowCount());
            // Nach einem Reload wird die vorherige Auswahl wiederhergestellt
            if (this.selectionToRestore) {
              this.selectionToRestore.forEach(id => this.gridApi.getRowNode(id)?.setSelected(true));
              this.selectionToRestore = null;
            }
          } else {
            params.fail();
          }
          if (this.$route.query.collapse === 'true') {
            this.triggerSidebarFromControl();
          }
        },
      };
    },
    server() {
      return {
        getData: async request => {
          this.checkIfFilterSupplied();
          const filters = this.getFiltersFromRequest(request);
          const orderBy =
            request.sortModel.length > 0
              ? request.sortModel
                  .map(sortObject => sortObject.colId + ' ' + sortObject.sort)
                  .join(',')
                  .replaceAll('.', '/')
              : ['abreisedatum'];
          this.lastOdataFilter = filters;
          const selectedReiseterminVariables = [...terminplanungSelectedReiseterminVariables.standard, 'id'];

          const expand = {
            metadata: {
              expand: {
                ziellandAbflughaefen: {},
                ziellandZielflughaefen: {},
                // Die Sammelbearbeitung schickt die gespeicherten Country Manager je Termin mit und prüft damit
                // die weiteren Partner vorab
                CountryManagers: { select: 'countryManagerKuerzel' },
                WeiterePartner: { select: 'countryManagerKuerzel' },
              },
            },
            pax: { select: ['min', 'max', 'ist', 'opt', 'anf', 'war', 'vor'] },

            reise: {},
          };

          const odataQuery = buildQuery({
            top: request.endRow - request.startRow,
            skip: request.startRow,
            filter: filters,
            orderBy,
            select: selectedReiseterminVariables,
            count: true,
            expand,
          });
          const response = await apiService.get('/Reisetermin', odataQuery);
          this.count = response.data.result['@odata.count'];
          this.regionFilterDisabled = false;
          const data = response.data.result.value;

          return {
            success: true,
            rows: data,
          };
        },
      };
    },
    getFiltersFromRequest(request) {
      this.gridApi?.deselectAll();
      const { filterModel } = request;
      const filterKeys = Object.keys(filterModel);

      let filters = filterKeys.map(filterKey => {
        const filterValues = filterModel[filterKey];

        const formattedKey = filterKey.replaceAll('.', '/');
        if (filterModel[filterKey].filterType === 'text') {
          if (['id', 'verkehrstagHin', 'pax/ist'].includes(formattedKey)) {
            if (request.filterModel[filterKey].filter === '-') {
              return { [formattedKey]: null };
            } else if (request.filterModel[filterKey].filter.includes('-')) {
              const min = parseInt(request.filterModel[filterKey].filter.split('-')[0]);
              const max = parseInt(request.filterModel[filterKey].filter.split('-')[1]);
              return { [formattedKey]: { le: max, ge: min } };
            } else if (request.filterModel[filterKey].filter[0] === '>') {
              const min = parseInt(request.filterModel[filterKey].filter.split('>')[1]);
              return { [formattedKey]: { gt: min } };
            } else if (request.filterModel[filterKey].filter[0] === '<') {
              const max = parseInt(request.filterModel[filterKey].filter.split('<')[1]);
              return { [formattedKey]: { lt: max } };
            }
            return { [formattedKey]: parseInt(filterValues.filter) };
          }
          return { [formattedKey]: { startswith: filterValues.filter } };
        } else if (filterModel[filterKey].filterType === 'date') {
          const dateFrom = new Date(filterValues.dateFrom.substring(0, 10));
          const dateTo = new Date(filterValues.dateTo.substring(0, 10));
          return {
            [formattedKey]: {
              ge: new Date(dateFrom),
              le: new Date(dateTo),
            },
          };
        } else if (filterModel[filterKey].filterType === 'set') {
          if (formattedKey === 'reiseterminkuerzel') {
            return { 'reise/reisekuerzel': { in: filterModel[filterKey].values } };
          }
          if (formattedKey === 'reiseterminSyncState') {
            const converter = {
              true: "reiseterminSyncState in ('AvailableInBlank','AvailableInBlankAndSyncedHotelleistungWithRelations','AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingente','AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingenteAndZusatzleistungen')",
              false: "reiseterminSyncState in ('NotAvailableInBlank')",
              null: "reiseterminSyncState in ('None')",
            };

            return filterModel[filterKey].values.map(item => converter[item]).join(' or ');
          } else if (formattedKey === 'reise/region') {
            const regionConverter = {
              EU: euRegions.filter(region => region !== 'Dach'),
              FERN: fernRegions,
              DACH: ['Dach'],
            };
            const regionValues = filterValues.values.map(value => regionConverter[value]).flat();
            return { [formattedKey]: { in: regionValues } };
          } else
            return `${formattedKey} in (${filterModel[filterKey].values
              .map(item => `'${item}'`)
              .join(',')}) eq true`;
        }
      });
      filters = [
        ...filters,
        {
          isDeleted: false,
        },
      ];
      if (this.odataFilterReiseterminkuerzel) {
        filters = [...filters, this.odataFilterReiseterminkuerzel];
      }

      if (this.vergangeneTermineAusblenden) {
        filters = [...filters, { abreisedatum: { ge: new Date() } }];
      }

      if (
        !filterKeys.includes('state') &&
        !this.odataFilterReiseterminkuerzel &&
        !filterKeys.includes('reiseterminSyncState') &&
        this.vergangeneTermineAusblenden
      ) {
        filters = [
          ...filters,
          "state in ('Vorgeschlagen','CmBestaetigt','Anlagebereit','Aufgelegt','CmAngefragt','CmAbgelehnt') eq true",
        ];
      }

      return filters;
    },

    updateStatusBar(displayedRowsCount) {
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setRowCount(displayedRowsCount);
      statusBarComponent?.setOdataCount(this.count);
    },
    checkIfFilterSupplied() {
      const usedFilters = Object.keys(this.gridApi?.getFilterModel()).filter(item => item !== 'state');
      const statusFilterValues = this.gridApi.getFilterInstance('state')?.appliedModel?.values;
      const defaultStatusFilterDiffers =
        statusFilterValues &&
        (statusFilterValues.length !== 6 ||
          !statusFilterValues.includes('Vorgeschlagen') ||
          !statusFilterValues.includes('Aufgelegt') ||
          !statusFilterValues.includes('Anlagebereit') ||
          !statusFilterValues.includes('CmBestaetigt') ||
          !statusFilterValues.includes('CmAngefragt') ||
          !statusFilterValues.includes('CmAbgelehnt'));

      this.isFilterSupplied =
        (this.gridApi &&
          (this.regionFilterSelected?.length > 0 ||
            !this.vergangeneTermineAusblenden ||
            this.odataFilterReiseterminkuerzel !== null)) ||
        usedFilters.length > 0 ||
        defaultStatusFilterDiffers;
    },
    resetAllFilters() {
      this.gridApi.setFilterModel(null);
      const statusFilterComponent = this.gridApi.getFilterInstance('state');
      statusFilterComponent.setModel({
        values: ['Vorgeschlagen', 'Aufgelegt', 'Anlagebereit', 'CmBestaetigt', 'CmAngefragt', 'CmAbgelehnt'],
      });

      let filterDate1 = this.gridApi.getFilterInstance('abreisedatum');
      let filterDate2 = this.gridApi.getFilterInstance('startdatum');
      let filterDate3 = this.gridApi.getFilterInstance('enddatum');
      this.selectedRegions = [];
      this.vergangeneTermineAusblenden = true;
      this.odataFilterReiseterminkuerzel = null;
      const query = { ...this.$route.query };
      delete query['reisetermine'];
      delete query['reise'];
      delete query['abreisedatumVon'];
      delete query['abreisedatumBis'];
      this.$router.push({ query });
      filterDate1.onDateRangeFilterChanged(null);
      filterDate2.onDateRangeFilterChanged(null);
      filterDate3.onDateRangeFilterChanged(null);
    },
    onReiseterminMetadataBearbeitet(response, fromSidebar = true) {
      const reiseterminkuerzel = Object.keys(response).map(
        key => response[key].reisekuerzel + '_' + response[key].reiseterminStartdatum.substring(0, 10)
      );
      this.updateChangedRow(response, reiseterminkuerzel, fromSidebar);
      this.gridApi.refreshCells({ force: true });
    },
    onInlandsflugBearbeitet(response) {
      const { reiseterminkuerzel, inlandsflug: inlandsflugBearbeitet } = response;
      this.gridApi.forEachNode(rowNode => {
        if (reiseterminkuerzel === rowNode.data.reiseterminkuerzel) {
          const updated = rowNode.data;
          // Im Grid sind die Inlandsflüge nicht geladen, daher Fallback auf leeres Array
          const inlandsfluege = updated.metadata?.inlandsfluege ?? [];
          const existiertBereits = inlandsfluege.some(
            inlandsflug => inlandsflug.id === inlandsflugBearbeitet.id
          );
          // Ist der Inlandsflug in der Zeile bereits vorhanden, wird er ersetzt
          // andernfalls wird er angehängt, damit die Zeile auch dann konsistent bleibt
          // wenn die Inlandsflüge im Grid noch nicht geladen waren
          updated.metadata.inlandsfluege = existiertBereits
            ? inlandsfluege.map(inlandsflug =>
                inlandsflug.id === inlandsflugBearbeitet.id ? inlandsflugBearbeitet : inlandsflug
              )
            : [...inlandsfluege, inlandsflugBearbeitet];
          rowNode.updateData(updated);
        }
      });
    },
    onDeletedInlandsflug(response) {
      const { id, reiseterminkuerzel } = response;
      this.gridApi.forEachNode(rowNode => {
        if (reiseterminkuerzel === rowNode.data.reiseterminkuerzel) {
          const updated = rowNode.data;
          // Im Grid sind die Inlandsflüge nicht geladen, daher Fallback auf leeres Array
          const inlandsfluege = updated.metadata?.inlandsfluege ?? [];
          updated.metadata.inlandsfluege = inlandsfluege.filter(inlandsflug => inlandsflug.id !== id);
          rowNode.updateData(updated);
        }
      });
    },
    onCreatedInlandsflug(response) {
      const { reiseterminkuerzel, newInlandsflug } = response;
      this.gridApi.forEachNode(rowNode => {
        if (reiseterminkuerzel === rowNode.data.reiseterminkuerzel) {
          const updated = rowNode.data;
          // Im Grid sind die Inlandsflüge nicht geladen, daher Fallback auf leeres Array
          const inlandsfluege = updated.metadata?.inlandsfluege ?? [];
          updated.metadata.inlandsfluege = [...inlandsfluege, newInlandsflug];
          rowNode.updateData(updated);
        }
      });
    },
    onReiseterminValuesBearbeitetMultiple() {
      this.gridApi.onFilterChanged();
    },
    onReiseterminMetadataBearbeitetMultiple() {
      this.gridApi.onFilterChanged();
    },
    onReiseterminBearbeitet(
      reiseterminkuerzel,
      thLangstrecke,
      thInlandsflug,
      istZusatztermin,
      istInKlaerung
    ) {
      this.gridApi.forEachNode(rowNode => {
        if (reiseterminkuerzel.includes(rowNode.data.reiseterminkuerzel)) {
          const updated = rowNode.data;
          updated.thLangstrecke = thLangstrecke;
          updated.istZusatztermin = istZusatztermin;
          updated.thInlandsflug = thInlandsflug;
          updated.istInKlaerung = istInKlaerung;
          rowNode.updateData(updated);
        }
      });
    },
    updateChangedRow(response, reiseterminkuerzel, fromSidebar) {
      this.gridApi.forEachNode(rowNode => {
        if (reiseterminkuerzel.includes(rowNode.data.reiseterminkuerzel)) {
          const inlandsfluege = rowNode.data.metadata.inlandsfluege?.slice();
          const bisherigeCountryManagers = rowNode.data.metadata.countryManagers;
          const { startdatum } = rowNode.data;
          const metadata = response[startdatum];
          const ziellandAbflughaefen = metadata.ziellandAbflughaefenIataCodes.map(iataCode => {
            return {
              flughafenIataCode: iataCode,
              isPrio: iataCode === metadata.ziellandAbflughafenPrio,
            };
          });
          const ziellandZielflughaefen = metadata.ziellandZielflughaefenIataCodes.map(iataCode => {
            return {
              flughafenIataCode: iataCode,
              isPrio: iataCode === metadata.ziellandZielflughafenPrio,
            };
          });
          delete metadata['ziellandAbflughaefenIataCodes'];
          delete metadata['ziellandZielflughaefenIataCodes'];

          const updated = rowNode.data;
          updated.metadata = metadata;
          updated.metadata.restriktionenHinRollover =
            metadata.restriktionenHinRollover == null
              ? metadata.restriktionenHinRollover
              : Boolean(metadata.restriktionenHinRollover);
          updated.metadata.restriktionenRueckRollover =
            metadata.restriktionenRueckRollover == null
              ? metadata.restriktionenRueckRollover
              : Boolean(metadata.restriktionenRueckRollover);
          updated.metadata.inlandsfluege = inlandsfluege;
          updated.metadata.ziellandAbflughaefen = ziellandAbflughaefen;
          updated.metadata.ziellandZielflughaefen = ziellandZielflughaefen;
          updated.metadata.weiterePartner = (metadata.weiterePartner ?? []).map(kuerzel =>
            typeof kuerzel === 'string' ? { countryManagerKuerzel: kuerzel } : kuerzel
          );
          // Fehlen die Country Manager in der Antwort, bleiben die bisherigen der Zeile stehen
          updated.metadata.countryManagers =
            metadata.countryManagerKuerzel?.map(kuerzel => ({ countryManagerKuerzel: kuerzel })) ??
            bisherigeCountryManagers;
          rowNode.updateData(updated);
          if (fromSidebar) {
            this.activeReisetermin = rowNode.data;
          }
        }
      });
    },
    onRowDoubleClicked(params) {
      this.gridApi.deselectAll();
      this.gridApi.forEachNode(rowNode => {
        if (rowNode.rowIndex === params.rowIndex) {
          rowNode.setSelected(true);
          this.selectedRowID = params.rowIndex;
        }
      });
      this.activeReisetermin = params.data;
      this.collapseSidebar();
    },
    triggerSidebarFromControl() {
      this.gridApi.deselectAll();
      this.gridApi.forEachNode(rowNode => {
        if (rowNode.rowIndex === 0) {
          rowNode.setSelected(true);
          this.selectedRowID = 0;
          this.activeReisetermin = rowNode.data;
        }
      });
      this.collapseSidebar();
    },
    onReiseterminDeleted() {
      this.gridApi.onFilterChanged();
    },
    // Führt den Statuswechsel für eine Liste von Reiseterminen aus
    // Verworfen braucht vorab noch Grund und Kommentar, erst danach folgt der Statuswechsel
    onStatusAendern({ reiseterminIds, status, grund, kommentar }) {
      if (status === 'Verworfen') {
        apiService
          .post('Reisetermin/VerworfenData', { reiseterminIds, grund, kommentar })
          .then(() => this.changeStateReisetermine(reiseterminIds, status));
      } else {
        this.changeStateReisetermine(reiseterminIds, status);
      }
    },
    onStatusWechseln({ reiseterminId, reiseterminIds, option }) {
      this.statuswechselPending = { reiseterminIds: reiseterminIds ?? [reiseterminId], option };
      this.$bvModal.show('modalStatusBestaetigen');
    },
    confirmStatusWechsel() {
      const { reiseterminIds, option } = this.statuswechselPending;
      // Der Speichern-Button bleibt während des Ausblendens klickbar, der zweite Klick läuft hier ins Leere
      if (!reiseterminIds?.length) {
        return;
      }
      this.onStatusAendern({ reiseterminIds, status: option?.value });
      this.statuswechselPending = {};
    },
    onTerminVerwerfen({ reiseterminId, reiseterminIds }) {
      this.statuswechselPending = { reiseterminIds: reiseterminIds ?? [reiseterminId] };
      this.$bvModal.show('modalTerminVerwerfen');
    },
    confirmTerminVerwerfen({ grund, kommentar }) {
      const { reiseterminIds } = this.statuswechselPending;
      if (!reiseterminIds?.length) {
        return;
      }
      this.onStatusAendern({ reiseterminIds, status: 'Verworfen', grund, kommentar });
      this.statuswechselPending = {};
    },
    onDeleteReisetermin() {
      this.hardDeleteReisetermine(this.idsSelectedReisetermine);
    },
    onClickDeleteReisetermine() {
      this.$bvModal.show('modalReisetermineLoeschen');
    },
    confirmDeleteReisetermine() {
      this.$bvModal.hide('modalReisetermineLoeschen');
      this.onDeleteReisetermin();
    },
    changeStateReisetermine(reiseterminIds, endState) {
      apiService.put('Reisetermin/ChangeState', { reiseterminIds, endState }).then(res => {
        this.toast('Status erfolgreich bearbeitet.');
        this.gridApi.forEachNode(rowNode => {
          if (reiseterminIds.includes(rowNode.data.id)) {
            const updated = rowNode.data;
            updated.state = endState;
            rowNode.updateData(updated);
          }
        });
        if (this.allSelectedRows?.length > 1) {
          this.allSelectedRows = [];
          this.gridApi.deselectAll();
        }
        if (reiseterminIds.includes(this.activeReisetermin?.id)) {
          this.$refs.terminplanungSidebar?.reloadReisetermin();
        }
      });
    },
    hardDeleteReisetermine(reiseterminIds) {
      apiService.delete('Reisetermin/HardDelete', { reiseterminIds }).then(res => {
        this.toast('Erfolgreich gelöscht.');
        this.gridApi.onFilterChanged();
        this.allSelectedRows = [];
      });
    },
    changeRegionFilterInstance(selectedRegions) {
      if (!this.gridApi) return;

      const regionsFilter = this.gridApi.getFilterInstance('reise.region');
      if (!regionsFilter) return;

      if (selectedRegions.length > 0) {
        regionsFilter.setModel({ values: selectedRegions });
      } else {
        regionsFilter.setModel({});
      }
      this.gridApi.onFilterChanged();
    },
    updateStatusFilter(vergangeneTermineAusblenden) {
      if (!this.gridApi) return;

      const statusFilterComponent = this.gridApi.getFilterInstance('state');

      if (vergangeneTermineAusblenden) {
        statusFilterComponent.setModel({
          values: this.defaultStatusFilter,
        });
      } else {
        const extendedStatusFilter = [...this.defaultStatusFilter, 'Durchgefuehrt', 'Durchfuehrung'];
        statusFilterComponent.setModel({
          values: extendedStatusFilter,
        });
      }
      this.gridApi.onFilterChanged();
    },
  },
};
</script>

<style scoped>
:deep(.ag-theme-alpine) {
  font-family: 'Poppins';
}

:deep(.ag-theme-alpine .ag-header-cell) {
  font-weight: 500;
  font-size: 14px;
}

:deep(.ag-cell),
:deep(.ag-header-cell) {
  padding: 0px 0.5rem;
}

:deep(.dropdown-menu) {
  min-width: 260px;
  margin-top: 8px;
}
</style>
